import { createTheme, ThemeOptions } from '@mui/material/styles'
import { heIL } from '@mui/x-data-grid/locales'

// Extend the Material UI theme types
declare module '@mui/material/styles' {
  interface Palette {
    sidebar?: {
      active: string
      hover: string
      border: string
    }
  }

  interface PaletteOptions {
    sidebar?: {
      active: string
      hover: string
      border: string
    }
  }

  interface TypeBackground {
    sidebar?: string
  }
}

// Custom color palette
const lightPalette = {
  primary: {
    main: '#487CCA',
    light: '#6B9CD6',
    dark: '#2E5A8A',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#003F61',
    light: '#005A8A',
    dark: '#002A40',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#F8FAFC',
    paper: '#FFFFFF',
    sidebar: '#EFF2F7',
  },
  text: {
    primary: '#003F61',
    secondary: '#4A5568',
    disabled: '#A0AEC0',
  },
  sidebar: {
    active: '#487CCA',
    hover: '#E3F5FF',
    border: '#E2E8F0',
  },
  success: {
    main: '#48BB78',
    light: '#9AE6B4',
    dark: '#38A169',
  },
  warning: {
    main: '#ED8936',
    light: '#FBD38D',
    dark: '#DD6B20',
  },
  error: {
    main: '#F56565',
    light: '#FEB2B2',
    dark: '#E53E3E',
  },
  info: {
    main: '#4299E1',
    light: '#90CDF4',
    dark: '#3182CE',
  },
}

const darkPalette = {
  primary: {
    main: '#63B3ED',
    light: '#90CDF4',
    dark: '#3182CE',
    contrastText: '#1A202C',
  },
  secondary: {
    main: '#81E6D9',
    light: '#B2F5EA',
    dark: '#4FD1C7',
    contrastText: '#1A202C',
  },
  background: {
    default: '#192336',
    paper: '#192336',
    sidebar: '#2D3748',
  },
  text: {
    primary: '#F7FAFC',
    secondary: '#E2E8F0',
    disabled: '#718096',
  },
  sidebar: {
    active: '#63B3ED',
    hover: '#4A5568',
    border: '#4A5568',
  },
  success: {
    main: '#68D391',
    light: '#9AE6B4',
    dark: '#48BB78',
  },
  warning: {
    main: '#F6AD55',
    light: '#FBD38D',
    dark: '#ED8936',
  },
  error: {
    main: '#FC8181',
    light: '#FEB2B2',
    dark: '#F56565',
  },
  info: {
    main: '#63B3ED',
    light: '#90CDF4',
    dark: '#4299E1',
  },
}

// Common theme options
const commonThemeOptions: ThemeOptions = {
  typography: {
    fontFamily:
      '"Rubik", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  components: {
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': {
            outline: 'none',
            boxShadow: 'none',
          },
          '&:focus': {
            outline: 'none',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.Mui-focusVisible, &:focus': {
            outline: 'none',
            boxShadow: 'none',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '&.Mui-focusVisible, &:focus': {
            outline: 'none',
            boxShadow: 'none',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          '&:focus': {
            outline: 'none',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          },
          // הסרת ה-outline בזמן פוקוס (focus)
          '&.Mui-focusVisible, &:focus': {
            outline: 'none',
            boxShadow: 'none',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
        },
      },
    },
  },
}

// The grid texts (footer, filter panel, column menu) of every data grid come from this locale.
export const GRID_LOCALE = heIL

// A grid's own `localeText` prop replaces the theme's whole object, so a grid that sets one spreads these first.
export const GRID_LOCALE_TEXT =
  heIL.components?.MuiDataGrid?.defaultProps?.localeText

// Create light theme
export const lightTheme = createTheme(
  {
    ...commonThemeOptions,
    palette: {
      mode: 'light',
      ...lightPalette,
    },
  },
  GRID_LOCALE
)

// Create dark theme
export const darkTheme = createTheme(
  {
    ...commonThemeOptions,
    palette: {
      mode: 'dark',
      ...darkPalette,
    },
  },
  GRID_LOCALE
)

// Export the default theme for backward compatibility
export const theme = lightTheme
